import type React from "react";
import { useEffect } from "react";
import { useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import EventSelector from "./components/EventSelector";
import AssignmentGrid from "./components/AssignmentGrid";
import EmptyState from "./components/EmptyState";
import type {
  AssignmentData,
  CrewMember,
  Role,
  Stream,
} from "./types/crew-assignment";
import apiService from "../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";

const BulkCrewAssignment: React.FC = () => {
  const [selectedEvents, setSelectedEvents] = useState<Stream[]>([]);
  const [assignments, setAssignments] = useState<AssignmentData>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiService.get<{ results: Role[] }>(
          "/api/crews/roles/"
        );
        setRoles(response.data.results || []);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        setRoles([]);
      }
    };

    fetchRoles();
  }, []);

  // Update assignment for a specific event and role
  const updateAssignment = (
    eventId: number,
    roleId: number,
    crewMembers: CrewMember[]
  ) => {
    setAssignments((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        [roleId]: crewMembers,
      },
    }));
  };

  const getAssignment = (eventId: number, roleId: number): CrewMember[] => {
    return assignments[eventId]?.[roleId] || [];
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (selectedEvents.length === 0) {
        showErrorToast("Please select at least one event");
        return;
      }
      if (!roles || roles.length === 0) {
        showErrorToast("Roles are still loading. Please wait.");
        return;
      }

      const formattedData: Array<{
        stream: number;
        crew_member_id: number;
        assignment: Array<{ role: string; notes: string }>;
      }> = [];

      Object.entries(assignments).forEach(([eventId, eventAssignments]) => {
        const event = selectedEvents.find((e) => e.id.toString() === eventId);
        if (!event) return;

        Object.entries(eventAssignments).forEach(([roleId, crewMembers]) => {
          if (!crewMembers || crewMembers.length === 0) return;

          const role = roles.find((r) => r.id.toString() === roleId);
          if (!role) return;

          crewMembers.forEach((crewMember) => {
            formattedData.push({
              stream: Number(eventId),
              crew_member_id: Number(crewMember.id),
              assignment: [
                {
                  role: role.name,
                  notes: "",
                },
              ],
            });
          });
        });
      });

      if (formattedData.length === 0) {
        showErrorToast("No crew assignments to submit");
        return;
      }

      // API call
      const response = await apiService.post(
        "/api/call-sheets/callsheet/bulk-create/",
        formattedData
      );

      if (response.status >= 200 && response.status < 300) {
        showSuccessToast("Assignments successfully submitted!");
        setAssignments({});
        setSelectedEvents([]);
      } else {
        console.error("Submission failed:", response);
        showErrorToast("Failed to submit assignments");
      }
    } catch (error) {
      console.error("Submission failed:", error);
      showErrorToast("Failed to submit assignments");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate total assignments for summary

  const totalAssignments = useMemo(() => {
    return Object.values(assignments).reduce((total, eventAssignments) => {
      return (
        total +
        Object.values(eventAssignments).reduce((eventTotal, crewList) => {
          return eventTotal + crewList.length;
        }, 0)
      );
    }, 0);
  }, [assignments]);

  return (
    <Box sx={{ overflow: "auto" }}>
      <Paper sx={{ p: 3, boxShadow: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Bulk Crew Assignment
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Assign crew members to roles across multiple events
        </Typography>

        <EventSelector
          selectedEvents={selectedEvents}
          onEventsChange={setSelectedEvents}
        />

        {selectedEvents.length > 0 ? (
          <>
            <AssignmentGrid
              selectedEvents={selectedEvents}
              roles={roles}
              assignments={assignments}
              totalAssignments={totalAssignments}
              getAssignment={getAssignment}
              updateAssignment={updateAssignment}
              onSubmit={handleSubmit}
            />

            <Box>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmit}
                disabled={totalAssignments === 0 || isSubmitting}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : (
                  `Submit Assignments (${totalAssignments})`
                )}
              </Button>
            </Box>
          </>
        ) : (
          <EmptyState />
        )}
      </Paper>
    </Box>
  );
};

export default BulkCrewAssignment;
